import axios, { InternalAxiosRequestConfig, isAxiosError } from 'axios';
import useAuth from './useAuth';
import IErrorResponse from '#/types/API/expiredToken';
import ApiManager from '#/services/api/ApiManager';
import { spotifyTokenApiConfig } from '#/utils/constants';
import { IRefreshTokenData, ITokenResponse } from '#/types/API/token';
import { useNavigate } from 'react-router-dom';
import { ROUTE_NAMES } from '#/router/routes';
import { useMemo } from 'react';

const useProtectedSpotify = () => {
  const { retrieveAuth, storeAuth, removeAuth } = useAuth();

  const navigate = useNavigate();

  const protectedSpotify = useMemo(() => axios.create(), []);

  protectedSpotify.interceptors.request.use(
    (config) => {
      const auth = retrieveAuth();

      config.headers.Authorization = `Bearer ${auth?.token}`;

      return config;
    },

    (error) => {
      return Promise.reject(error);
    }
  );

  const refreshToken = async () => {
    const apiManager = new ApiManager(spotifyTokenApiConfig);
    const auth = retrieveAuth();
    if (auth?.refreshToken) {
      const data = {
        grant_type: 'refresh_token',
        refresh_token: auth.refreshToken,
        client_id: process.env.REACT_APP_SPOTIFY_CLIENT_ID,
      };
      const response = await apiManager.createEntity<
        IRefreshTokenData,
        ITokenResponse
      >('token', data, {
        'Content-Type': 'application/x-www-form-urlencoded',
      });
      if ('data' in response) {
        storeAuth({
          token: response.data.access_token,
          refreshToken: response.data.refresh_token,
        });
      }
    }
  };

  interface ICustomAxiosResponse extends InternalAxiosRequestConfig {
    _retry?: boolean;
  }

  protectedSpotify.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest: ICustomAxiosResponse = error.config;
      if (
        isAxiosError<IErrorResponse>(error) &&
        error.response?.data.error.status === 401 &&
        error.response.data.error.message === 'The access token expired'
      ) {
        if (originalRequest._retry) {
          removeAuth();
          navigate(ROUTE_NAMES.LOGIN);
        } else {
          originalRequest._retry = true;

          await refreshToken();

          return protectedSpotify(originalRequest);
        }
      } else {
        throw error;
      }
    }
  );
  return protectedSpotify;
};

export default useProtectedSpotify;
